import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogContent,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { CommonDialogFooter } from "@/shared/components/dialog/dialog-common";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  PutMatchOneToOneRequest,
  useBusinessCentralMatchOneToOnePutMutation,
} from "@/services/backend/business-central/api/match-one-to-one-api";
import { CompanySelect } from "@/services/backend/company/components/company-select";
import { Label } from "@/shared/components/ui/label";
import { BusinessCentralCompanySelect } from "@/services/backend/business-central/components/business-central-company-select";
import { MatchType } from "@/services/backend/business-central/types/match-type";
import { ServiceKindSelect } from "../../services/components/service-kind-select";
import { BusinessCentralGesecResourceSelect } from "./business-central-gesec-resource-select";
import { MatchOneToOneProps } from "./match-one-to-one-props";

export function MatchOneToOneCreateDialog(props: MatchOneToOneProps) {
  const { type } = props;
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<PutMatchOneToOneRequest>({
    type,
    gsmId: "",
    bcId: "",
  });
  const [put, { isLoading, error, isSuccess, reset }] =
    useBusinessCentralMatchOneToOnePutMutation();

  useEffect(() => {
    if (isSuccess) {
      setOpen(false);
      reset();
    }
  }, [isSuccess, reset]);

  const doCreate = () => {
    if (isLoading) return;
    put(request);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Zuordnung erstellen")}</DialogTitle>
        </DialogHeader>
        <InputSwitch {...props} request={request} setRequest={setRequest} />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter isLoading={isLoading} onClickAction={doCreate} />
      </DialogContent>
    </Dialog>
  );
}

function InputSwitch({
  request,
  setRequest,
  ...props
}: MatchOneToOneProps & {
  request: PutMatchOneToOneRequest;
  setRequest: (request: PutMatchOneToOneRequest) => void;
}) {
  switch (props.type) {
    case MatchType.CompanyToCompany:
      return (
        <div className="grid gap-2">
          <div className="grid content-start gap-1.5">
            <Label>{t("GSM Unternehmen")}</Label>
            <CompanySelect
              companyId={request.gsmId}
              onCompanyIdChange={(gsmId) => setRequest({ ...request, gsmId })}
            />
          </div>
          <div className="grid content-start gap-1.5">
            <Label>{t("Business Central Unternehmen")}</Label>
            <BusinessCentralCompanySelect
              value={request.bcId}
              onValueChange={(bcId) => setRequest({ ...request, bcId })}
            />
          </div>
        </div>
      );
    case MatchType.ServiceKindToResource:
      return (
        <div className="grid gap-2">
          <div className="grid content-start gap-1.5">
            <Label>{t("GSM Dienstleistung")}</Label>
            <ServiceKindSelect
              value={request.gsmId}
              onValueChange={(gsmId) => setRequest({ ...request, gsmId })}
            />
          </div>
          <div className="grid content-start gap-1.5">
            <Label>{t("Business Central Ressource")}</Label>
            <BusinessCentralGesecResourceSelect
              bcCompanyId={props.bcCompanyId}
              value={request.bcId}
              onValueChange={(bcId) => setRequest({ ...request, bcId })}
            />
          </div>
        </div>
      );
    default:
      return <span>{t("Nicht implementierter Zuordnungstyp")}</span>;
  }
}
